import { Controller } from "@hotwired/stimulus"
import { postJson, deleteRequest } from "../../utils/api"

export default class extends Controller {
  static values = {
    favouriteId: String,
    inTopTen: Boolean,
    topTenItemId: String
  }

  toggleItem(event) {
    event.preventDefault()

    // Toggle: if in Top 10, remove it; otherwise add it
    if (this.inTopTenValue) {
      this.removeItem(event)
    } else {
      this.addItem(event)
    }
  }

  addItem(event) {

    // Capture button reference before async call
    const button = event.currentTarget
    const icon = button.querySelector('i')

    const favouriteId = this.favouriteIdValue

    postJson('/favourites/add_to_top_ten', {
      favourite_id: favouriteId
    })
    .then(data => {
      if (data.success) {
        // If an item was removed (Top 10 was full), handle it first
        if (data.removed_item_id) {
          this.handleRemovedItem(data.removed_item_id, data.removed_favourite_id)
        }

        // Visual feedback - change icon to filled star
        if (icon) {
          icon.classList.remove('fa-regular')
          icon.classList.add('fa-solid')

          // Add a subtle scale animation using CSS class
          icon.classList.add('is-pulsing')
          setTimeout(() => {
            icon.classList.remove('is-pulsing')
          }, 200)
        }

        // Update the data attribute to mark it as in Top 10
        button.dataset.addToTopTenInTopTenValue = 'true'
        button.dataset.addToTopTenTopTenItemIdValue = data.top_ten_item_id

        // Insert the new item into the Top 10 section
        if (data.html) {
          this.insertTopTenItem(data.html, data.position)
          // Update badge count and position numbers
          this.updateBadgeCount()
          this.updatePositionBadges()
        }

        // Show success message
        if (data.category_name) {
          this.showToast(`Added to ${data.category_name} Top 10`)
        }

        // Remove the item from the favourites list (since Top 10 items are hidden from favs)
        this.removeFavouriteItem(button)
      } else {
        // Show error message
        console.error('Failed to add to Top 10:', data)
        alert('Could not add to Top 10. Please try again.')
      }
    })
    .catch(error => {
      console.error('Error adding to Top 10:', error)
      alert('An error occurred while adding to Top 10: ' + error.message)
    })
  }

  insertTopTenItem(html, position) {
    // Find the Top 10 list (always exists)
    const topTenSection = document.querySelector('ul[data-controller="top-ten-sortable"]')
    if (!topTenSection) return

    // Hide placeholder text if it exists
    const placeholder = topTenSection.nextElementSibling
    if (placeholder && placeholder.classList.contains('has-text-meta')) {
      placeholder.classList.add('is-hidden')
    }

    // Insert the new item
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html.trim()
    const newItem = tempDiv.firstElementChild

    if (!newItem) return

    // Always append to the end (service handles position logic)
    newItem.classList.add('fade-in-up')
    topTenSection.appendChild(newItem)

    // Trigger fade-in animation
    setTimeout(() => {
      newItem.classList.add('is-visible')
    }, 10)
  }

  updateBadgeCount() {
    const topTenSection = document.querySelector('ul[data-controller="top-ten-sortable"]')
    if (!topTenSection) return

    const count = topTenSection.querySelectorAll('li[data-top-ten-sortable-target="item"]').length
    const header = topTenSection.previousElementSibling

    if (!header) return

    let badge = header.querySelector('.badge')

    if (count > 0) {
      if (!badge) {
        // Create badge if it doesn't exist
        badge = document.createElement('div')
        badge.className = 'badge'
        header.appendChild(badge)
      }
      badge.textContent = count
    } else if (badge) {
      // Remove badge when count is 0
      badge.remove()
    }
  }

  updatePositionBadges() {
    const topTenSection = document.querySelector('ul[data-controller="top-ten-sortable"]')
    if (!topTenSection) return

    const items = topTenSection.querySelectorAll('li[data-top-ten-sortable-target="item"]')
    items.forEach((item, index) => {
      const badge = item.querySelector('.badge')
      if (badge) {
        badge.textContent = index + 1
      }
    })
  }

  handleRemovedItem(removedItemId, removedFavouriteId) {
    // Remove the item from the Top 10 DOM
    const topTenSection = document.querySelector('ul[data-controller="top-ten-sortable"]')
    if (!topTenSection) return

    const listItem = topTenSection.querySelector(`li[data-id="${removedItemId}"]`)
    if (listItem) {
      // Remove immediately without animation (will be replaced)
      listItem.remove()
    }

    // Update the star for the removed favourite back to outline
    const starButton = document.querySelector(`[data-add-to-top-ten-favourite-id-value="${removedFavouriteId}"]`)
    if (starButton) {
      const icon = starButton.querySelector('i')
      if (icon) {
        icon.classList.remove('fa-solid')
        icon.classList.add('fa-regular')
      }
      starButton.dataset.addToTopTenInTopTenValue = 'false'
      starButton.dataset.addToTopTenTopTenItemIdValue = ''
    }
  }

  removeItem(event) {
    const button = event.currentTarget
    const icon = button.querySelector('i')
    const topTenItemId = this.topTenItemIdValue

    if (!topTenItemId) {
      console.error('No top_ten_item_id found')
      return
    }

    deleteRequest(`/favourites/${topTenItemId}/remove_from_top_ten`)
    .then(data => {
      if (data.success) {
        // Visual feedback - change icon back to outline
        if (icon) {
          icon.classList.remove('fa-solid')
          icon.classList.add('fa-regular')

          // Add scale animation
          icon.classList.add('is-pulsing')
          setTimeout(() => {
            icon.classList.remove('is-pulsing')
          }, 200)
        }

        // Update the data attribute
        button.dataset.addToTopTenInTopTenValue = 'false'
        button.dataset.addToTopTenTopTenItemIdValue = ''

        // Remove the item from Top 10 section
        this.removeTopTenItem(topTenItemId)

        // Update badge count and position numbers
        this.updateBadgeCount()
        this.updatePositionBadges()

        // Show placeholder if Top 10 is now empty
        const topTenSection = document.querySelector('ul[data-controller="top-ten-sortable"]')
        const remainingItems = topTenSection?.querySelectorAll('li[data-top-ten-sortable-target="item"]')
        if (remainingItems && remainingItems.length === 0) {
          const placeholder = topTenSection.nextElementSibling
          if (placeholder && placeholder.classList.contains('has-text-meta')) {
            placeholder.classList.remove('is-hidden')
          }
        }

        // Add the item back to the favourites list
        if (data.html) {
          this.insertFavouriteItem(data.html)
        }
      } else {
        console.error('Failed to remove from Top 10:', data)
        alert('Could not remove from Top 10. Please try again.')
      }
    })
    .catch(error => {
      console.error('Error removing from Top 10:', error)
      alert('An error occurred while removing from Top 10: ' + error.message)
    })
  }

  removeTopTenItem(topTenItemId) {
    const topTenSection = document.querySelector('ul[data-controller="top-ten-sortable"]')
    if (!topTenSection) return

    const listItem = topTenSection.querySelector(`li[data-id="${topTenItemId}"]`)
    if (!listItem) return

    // Fade out and remove
    listItem.classList.add('fade-out-right', 'is-fading')

    setTimeout(() => {
      listItem.remove()
    }, 300)
  }

  removeFavouriteItem(button) {
    // Find the parent li element in the favourites list
    const listItem = button.closest('ul#favourites-list li')
    if (!listItem) return

    // Fade out and remove
    listItem.classList.add('fade-out-right', 'is-fading')

    setTimeout(() => {
      listItem.remove()
      // Update the favourites count badge
      this.updateFavouritesCount(-1)
    }, 300)
  }

  insertFavouriteItem(html) {
    // Find the favourites list
    const favouritesList = document.querySelector('ul#favourites-list')
    if (!favouritesList) return

    // Hide the "no favourites" placeholder if visible
    const placeholder = favouritesList.nextElementSibling
    if (placeholder && placeholder.classList.contains('has-text-meta')) {
      placeholder.classList.add('is-hidden')
    }

    // Create the new item
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html.trim()
    const newItem = tempDiv.firstElementChild

    if (!newItem) return

    // Add fade-in animation
    newItem.classList.add('fade-in-up')

    // Insert at the beginning of the list
    favouritesList.insertBefore(newItem, favouritesList.firstChild)

    // Trigger animation
    setTimeout(() => {
      newItem.classList.add('is-visible')
    }, 10)

    // Update the favourites count badge
    this.updateFavouritesCount(1)
  }

  updateFavouritesCount(delta) {
    // Find the Favs section header badge
    const favsHeader = document.querySelector('#favs-section-header')
    if (!favsHeader) return

    const badge = favsHeader.querySelector('.badge')
    if (badge) {
      const currentCount = parseInt(badge.textContent, 10) || 0
      badge.textContent = currentCount + delta
    }
  }

  showToast(message) {
    // Remove any existing toast
    const existingToast = document.querySelector('.toast-message')
    if (existingToast) {
      existingToast.remove()
    }

    // Create toast element
    const toast = document.createElement('div')
    toast.className = 'toast-message'
    toast.textContent = message
    document.body.appendChild(toast)

    // Trigger animation
    setTimeout(() => {
      toast.classList.add('is-visible')
    }, 10)

    // Remove after delay
    setTimeout(() => {
      toast.classList.remove('is-visible')
      setTimeout(() => {
        toast.remove()
      }, 300)
    }, 2500)
  }
}
